import { CircularProgressbarWithChildren } from 'react-circular-progressbar';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { NumberComponent } from '~/components/shared/NumberComponent';
import { useEffect, useState } from 'react';
import { useSubscription } from '~/components/contexts/SubscriptionProvider';

// Color interpolation helper function
const getProgressColor = (percentage: number): string => {
  // Ensure percentage is between 0 and 100
  const clampedPercentage = Math.max(0, Math.min(100, percentage));

  if (clampedPercentage >= 40) {
    // Interpolate between green (#36D399) and orange (#FFA500)
    const ratio = (clampedPercentage - 40) / 40;

    // RGB values for green and orange
    const greenRGB = { r: 54, g: 211, b: 153 }; // #36D399
    const orangeRGB = { r: 255, g: 165, b: 0 }; // #FFA500

    // Calculate interpolated values
    const r = Math.round(orangeRGB.r + ratio * (greenRGB.r - orangeRGB.r));
    const g = Math.round(orangeRGB.g + ratio * (greenRGB.g - orangeRGB.g));
    const b = Math.round(orangeRGB.b + ratio * (greenRGB.b - orangeRGB.b));

    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // Interpolate between orange (#FFA500) and red (#FF0000)
    const ratio = clampedPercentage / 40;

    // RGB values for orange and red
    const orangeRGB = { r: 255, g: 165, b: 0 }; // #FFA500
    const redRGB = { r: 255, g: 0, b: 0 }; // #FF0000

    // Calculate interpolated values
    const r = 255; // Red remains constant
    const g = Math.round(ratio * orangeRGB.g);
    const b = Math.round(ratio * orangeRGB.b);

    return `rgb(${r}, ${g}, ${b})`;
  }
};

export const MessageCounter = ({
  agentId,
  disabled = false,
  size = 'normal',
}: {
  agentId: string;
  disabled?: boolean;
  size?: 'normal' | 'large';
}) => {
  const { loading, subscriptionData } = useSubscription();

  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation('app');

  useEffect(() => {
    if (!loading && subscriptionData?.active) {
      const activeSubscription = subscriptionData.active;
      const { messages_used = 0, message_limit = 0 } = activeSubscription;

      if (messages_used >= message_limit && !disabled) {
        setIsModalOpen(true);
      }
    }
  }, [loading, subscriptionData, disabled]);

  if (loading) {
    return (
      <span className="loading loading-dots loading-md text-primary self-center"></span>
    );
  }

  // Check if we have subscription data
  if (!subscriptionData?.active) {
    return null;
  }

  const activeSubscription = subscriptionData.active;
  const { messages_used = 0, message_limit = 0 } = activeSubscription;

  let remainingPercentage = 0;
  if (message_limit > 0) {
    remainingPercentage = Math.max(
      0,
      ((message_limit - messages_used) / message_limit) * 100,
    );
  } else {
    remainingPercentage = 0;
  }

  const handleUpgradeNavigation = () => {
    navigate(`/agent/${agentId}/billing`);
    setIsModalOpen(false);
  };

  // Size classes
  const sizeClasses =
    size === 'large' ? 'h-fit w-fit text-base' : 'h-12 w-12 text-[9px]';
  const numberSize = size === 'large' ? 'text-xl' : 'text-[9px]';
  const progressbarStroke = size === 'large' ? 12 : 8;

  const buttonContent = (
    <button
      className={`mb-3 ${sizeClasses} transition-all duration-150 ${
        !disabled ? 'cursor-pointer hover:scale-110' : 'cursor-default'
      } flex flex-col items-center justify-center`}
      disabled={disabled}
      type="button"
      tabIndex={disabled ? -1 : 0}
      style={disabled ? { pointerEvents: 'none', opacity: 0.7 } : {}}
    >
      <div
        className="flex items-center justify-center p-1"
        style={
          size === 'large'
            ? { width: 130, height: 130 }
            : { width: 48, height: 48 }
        }
      >
        <CircularProgressbarWithChildren
          value={remainingPercentage}
          circleRatio={1}
          strokeWidth={size === 'large' ? 6 : progressbarStroke}
          counterClockwise
          styles={{
            path: {
              stroke: getProgressColor(remainingPercentage),
              strokeLinecap: 'butt',
            },
            trail: { stroke: '#C4C4C4', strokeLinecap: 'butt' },
          }}
        >
          {size === 'large' ? (
            <span className="flex items-center justify-center text-lg text-sm font-semibold">
              {messages_used}/{message_limit}
            </span>
          ) : (
            <NumberComponent
              size={numberSize}
              totalNumber={message_limit - messages_used}
            />
          )}
        </CircularProgressbarWithChildren>
      </div>
      <p className={size === 'large' ? 'text-sm font-semibold' : 'text-[9px]'}>
        {size === 'large' ? t(`messages_large`) : t(`messages`)}
      </p>
    </button>
  );

  return (
    <>
      {disabled ? (
        buttonContent
      ) : (
        <Link
          to={`/agent/${agentId}/billing`}
          tabIndex={-1}
          style={{ display: 'inline-block' }}
        >
          {buttonContent}
        </Link>
      )}
      {isModalOpen && (
        <div className="modal-open modal">
          <div className="modal-box">
            <h3 className="text-lg font-bold">
              {t(
                'app:message_limit_reached_title',
                "You've reached your message limit!",
              )}
            </h3>
            <p className="py-4">
              {t(
                'app:message_limit_reached_body',
                "You've used all the messages in your current plan. While you can still access the platform, the AI agent will not be able to respond to new messages. To continue enjoying uninterrupted AI assistance, please upgrade your subscription.",
              )}
            </p>
            <div className="modal-action">
              <button
                className="btn btn-primary rounded-full"
                onClick={handleUpgradeNavigation}
              >
                {t('app:upgrade_plan_button', 'Upgrade Plan')}
              </button>
              <button className="btn" onClick={() => setIsModalOpen(false)}>
                {t('app:close_button', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
